import { PaymentGatewayIntegration } from '../..';

import cvc from './elements/cvc.vue';
import creditCard from './elements/creditCard.vue';
import expiry from './elements/expiry.vue';
import Wrapper from '@/Render';

export default class Paysafe extends PaymentGatewayIntegration {
    public name = 'Paysafe';
    public scriptUrl = 'https://hosted.paysafe.com/js/v1/latest/paysafe.min.js';
    public fields = {
        cvc,
        creditCard,
        expiry,
    };

    public selectors = {
        creditCardSelector: '',
        expirySelector: '',
        cvcSelector: '',
    };

    public integrationFields = {
        paysafe: {},
        setup: {},
    };

    constructor(vm: Wrapper, settings: any) {
        super(vm, settings);

        this.isCvcOptional = false;
        this.createSlectors();
    }

    public createSlectors() {
        const salt = Math.round(Math.random() * 10000);
        this.selectors.creditCardSelector = `#cardNumberPS_${salt}`;
        this.selectors.expirySelector = `#expiryDatePS_${salt}`;
        this.selectors.cvcSelector = `#cvvPS_${salt}`;
    }

    public async created() {
        const setup = {
            environment: this.integrationData.env,
            initializationTimeout: 30000,
            fields: {
                cardNumber: {
                    selector: this.selectors.creditCardSelector,
                    placeholder: '**** **** **** ****',
                    separator: ' ',
                },
                expiryDate: {
                    selector: this.selectors.expirySelector,
                    placeholder: 'MM / YY',
                },
                cvv: {
                    selector: this.selectors.cvcSelector,
                    placeholder: 'CVV',
                    optional: true,
                },
            },
            style: (this.integrationStyles as any)[this.vm.styleVariant].styles || {},
        };
        const that = this;
        this.integrationFields.setup = setup;
        Promise.resolve(new Promise((resolve) => {
            const watcher = this.vm.$watch(function() {
                return this.loadingCounter;
            }, (val) => {
                if (val === 3) {
                    Object.keys(that.fields).forEach((field) => {
                        (that.vm.$refs[field] as any).loading = true;
                    });
                    resolve(watcher);
                }
            });
        })).then((watcher: any) => {
            this.vm.loadingCounter = 0;
            watcher();

            const fieldSetup = (this.integrationFields.setup as any);
            fieldSetup.fields.cardNumber.selector = this.integrationData.creditCardSelector
                || this.selectors.creditCardSelector;
            fieldSetup.fields.expiryDate.selector = this.integrationData.expirySelector
                || this.selectors.expirySelector;
            fieldSetup.fields.cvv.selector = this.integrationData.cvcSelector || this.selectors.cvcSelector;
            this.vm.$nextTick(() => {
                window.paysafe.fields.setup(this.integrationData.api_key, this.integrationFields.setup,
                    (instance, error) => {
                    if (!error) {
                        this.integrationFields.paysafe = instance;
                        Object.keys(that.fields).forEach((field) => {
                            (that.vm.$refs[field] as any).onIntegrationData(instance);
                        });
                    } else {
                        this.vm.failed = true;
                        console.error(error);
                    }
                });
            });
        });

        await this.loadScript();
        if (!window.paysafe) {
            this.vm.failed = true;
        }

        if (!this.vm.failedReinitAttempts) {
            const failedWatcher = this.vm.$watch(function() {
                return this.failedReinitAttempts;
            }, function(attempts) {
                const maxAttempts = that.integrationData.onFailRetryAttempts || 3;
                if (maxAttempts >= attempts) {
                    this.retryInitIntegration();
                } else {
                    destroyWatcher();
                }
            });

            const destroyWatcher = () => {
                failedWatcher();
            };
        }

        this.vm.loading = false;
    }

    public async serialize(additionalsGot: any) {
        const validation = this.vm.validate(true);
        const additionals = this.preSerialize(additionalsGot);
        if (validation.isValid) {
            const data: any = await new Promise((resolve) => {
                (this.integrationFields.paysafe as any).tokenize(additionals, (instance, error, result) => {
                    if (error) {
                        resolve({
                            error: true,
                            message: error,
                        });
                    }
                    resolve(result);
                });
            });
            if (!data.error) {
                return this.postSerialize(additionalsGot, {
                    token_data: data.token,
                });
            } else {
                const fieldsMapping = {
                    'card number': 'creditCard',
                    'expiry date': 'expiry',
                    'cvv': 'cvc',
                };
                validation.isValid = false;
                validation.commonError = data.message.detailedMessage;
                if (data.message.detailedMessage) {
                    const match = data.message.detailedMessage.match(/cvv|expiry date|card number/);
                    if (match) {
                        validation.validation[fieldsMapping[match[0]]] = false;
                        (this.vm.$refs[fieldsMapping[match[0]]] as any).valid = false;
                        (this.vm.$refs[fieldsMapping[match[0]]] as any).hardValidation = true;
                        (this.vm.$refs[fieldsMapping[match[0]]] as any).$emit('valid', false);
                    }
                }
                throw validation;
            }
        } else {
            throw validation;
        }
    }
}
